import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  TextField,
  List,
  ListItem,
  Avatar,
  Snackbar,
  Divider,
  IconButton,
  Chip,
  Badge,
  Card,
  CardContent
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DateTime } from 'luxon';

import Nav from "../../components/Nav/Nav";
import { UserContext } from "../../App";
import MessageApi from "../../api/MessageApi";
import { FullMessageDto, CreateMessageDto } from "../../interfaces/MessageInterfaces";
import WebSocketService from "../../utils/WebSocketService";

import "./UserMessagesView.scss";

const UserMessagesView = () => {
  const user = useContext(UserContext);
  
  // State
  const [messages, setMessages] = useState<FullMessageDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load initial messages
  const loadMessages = useCallback(async (from: number = 0, to: number = 15, prepend: boolean = false) => {
    if (!user) return;

    try {
      if (!prepend) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const fetchedMessages = await MessageApi.getMessages(from, to);
      
      if (fetchedMessages.length === 0) {
        setHasMoreMessages(false);
        return;
      }

      // Sort messages by creation date (oldest first)
      fetchedMessages.sort((a, b) => 
        new Date(a.Message.Message_CreatedAt).getTime() - new Date(b.Message.Message_CreatedAt).getTime()
      );

      if (prepend) {
        // When loading older messages, prepend them and maintain scroll position
        const container = messagesContainerRef.current;
        const oldScrollHeight = container?.scrollHeight || 0;
        
        setMessages(prev => [...fetchedMessages, ...prev]);
        
        // Restore scroll position after new messages are loaded
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - oldScrollHeight;
            container.scrollTop = scrollPositionRef.current + scrollDiff;
          }
        }, 100);
      } else {
        setMessages(fetchedMessages);
        // Scroll to bottom for initial load
        setTimeout(scrollToBottom, 100);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user]);

  // Load more messages when scrolled to top
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || loadingMore || !hasMoreMessages) return;

    const container = messagesContainerRef.current;
    if (container.scrollTop === 0 && messages.length > 0) {
      // Store current scroll position
      scrollPositionRef.current = container.scrollTop;
      // Load older messages
      loadMessages(messages.length, messages.length + 15, true);
    }
  }, [loadingMore, hasMoreMessages, messages.length, loadMessages]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !user) return;

    try {
      setSending(true);
      
      const createMessageDto: CreateMessageDto = {
        Message_Body: newMessage.trim(),
        User_guid: user.User_guid
      };

      const sentMessage = await MessageApi.sendMessage(createMessageDto);
      
      // Add message to local state (it will also come via WebSocket)
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      setTimeout(scrollToBottom, 100);
      
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format message timestamp
  const formatMessageTime = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const dt = DateTime.fromJSDate(date);
    const now = DateTime.now();
    
    if (dt.hasSame(now, 'day')) {
      return dt.toFormat('h:mm a');
    } else if (dt > now.minus({ days: 7 })) {
      return dt.toFormat('ccc h:mm a');
    } else {
      return dt.toFormat('MMM d, h:mm a');
    }
  };

  // Get sender name
  const getSenderName = (message: FullMessageDto) => {
    if (message.Manager) {
      return `${message.Manager.Manager_FirstName} ${message.Manager.Manager_LastName}`;
    } else if (message.Message.Message_Source === 'system') {
      return 'TX Ticket Doctor';
    } else {
      return `${message.User.User_FirstName} ${message.User.User_LastName}`;
    }
  };

  // Check if message is from current user — an automated send shares the client's own
  // Manager_id: null shape, so it has to be told apart by Message_Source or it renders as if
  // the client had sent it to themselves.
  const isMyMessage = (message: FullMessageDto) => {
    return (
      user &&
      message.Message.User_guid === user.User_guid &&
      !message.Manager &&
      message.Message.Message_Source !== 'system'
    );
  };

  // WebSocket effects
  useEffect(() => {
    if (!user) return;

    const handleNewMessage = (message: FullMessageDto) => {
      setMessages(prev => {
        // Check if message already exists to avoid duplicates
        const exists = prev.some(m => m.Message.Message_guid === message.Message.Message_guid);
        if (exists) return prev;
        
        return [...prev, message];
      });
      setTimeout(scrollToBottom, 100);
    };

    // Connect to WebSocket and set up listeners
    WebSocketService.connect();
    WebSocketService.joinUserRoom(user.User_guid);
    WebSocketService.onNewMessage(handleNewMessage);

    return () => {
      WebSocketService.offNewMessage(handleNewMessage);
      WebSocketService.leaveUserRoom(user.User_guid);
    };
  }, [user?.User_guid]); // Only depend on User_guid to prevent re-runs

  // Initial load
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  /*
   * Which messages were re-sent, held for the length of this visit.
   *
   * The marker has to outlive the read receipt. The first version marked the message read as soon
   * as the thread rendered, which re-rendered without the marker roughly one frame later - so the
   * label meant to tell the client "this is the message we sent you again" was never on screen long
   * enough to read. The logic was right and the feature was useless.
   *
   * So the ids are captured once and the marker is drawn from THIS, never from the live flag. The
   * receipt goes to the server behind it. The client sees the marker for this visit and it is gone
   * the next time they come back, which is what the label is for.
   */
  const [resentOnArrival, setResentOnArrival] = useState<Set<string>>(new Set());

  useEffect(() => {
    const unread = messages.filter(
      (message) => !!message.Manager && message.Message.Message_UserSeen === false,
    );
    if (unread.length === 0) return;

    // Add rather than replace: a re-send can arrive by WebSocket while the thread is open, and it
    // should be marked too rather than replacing what is already showing.
    setResentOnArrival((prev) => {
      const next = new Set(prev);
      unread.forEach((message) => next.add(message.Message.Message_guid));
      return next;
    });

    /*
     * The read receipt, sent quietly. Failures are ignored on purpose: this is not the client's
     * message, and if it does not land the marker simply shows once more. Blocking the thread on
     * it, or surfacing an error about it, would both be worse.
     *
     * State is deliberately NOT updated on success. Doing so would clear the marker mid-visit,
     * which is the defect this whole arrangement exists to avoid.
     */
    void (async () => {
      for (const message of unread) {
        try {
          await MessageApi.markMessageAsSeen(message.Message.Message_guid, true);
        } catch {
          return;
        }
      }
    })();
  }, [messages]);

  // Global cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up any remaining WebSocket listeners on unmount
      if (user) {
        WebSocketService.leaveUserRoom(user.User_guid);
      }
    };
  }, []);

  // Add scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  if (!user) {
    return (
      <>
        <Nav />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">Please log in to view messages.</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Nav />
      <Container maxWidth="md" className="user-messages-container">
        <Paper elevation={2} className="messages-paper">
          {/* Header */}
          <Box className="messages-header">
            <Typography variant="h5" component="h1">
              Messages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Chat with our support team
            </Typography>
          </Box>

          {/* Messages Container */}
          <Box 
            ref={messagesContainerRef}
            className="messages-scroll-container"
          >
            {/* Loading indicator for older messages */}
            {loadingMore && (
              <Box className="loading-more">
                <CircularProgress size={20} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Loading older messages...
                </Typography>
              </Box>
            )}

            {/* Messages List */}
            {loading && messages.length === 0 ? (
              <Box className="messages-loading">
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading messages...
                </Typography>
              </Box>
            ) : messages.length === 0 ? (
              <Box className="no-messages">
                <Typography variant="body2" color="text.secondary">
                  No messages yet. Start a conversation with our support team!
                </Typography>
              </Box>
            ) : (
              <List className="messages-list">
                {messages.map((message, index) => {
                  const isMine = isMyMessage(message);
                  const senderName = getSenderName(message);
                  const isManager = !!message.Manager || message.Message.Message_Source === 'system';
                  /*
                   * Read from the arrival snapshot, not the live flag.
                   *
                   * The flag is cleared on the server as soon as the thread opens, so drawing from
                   * it would remove the marker before the client could read it. The snapshot is
                   * built from `Message_UserSeen === false`, which only the chase ladder ever
                   * writes - the rest of the history is null, and "not true" would badge every
                   * message the client has ever received.
                   */
                  const resent = isManager && resentOnArrival.has(message.Message.Message_guid);
                  
                  return (
                    <ListItem 
                      key={message.Message.Message_guid}
                      className={`message-item ${isMine ? 'my-message' : 'their-message'}`}
                    >
                      <Box className={`message-wrapper ${isMine ? 'my-message' : 'their-message'}`}>
                        {isMine && (
                          <Avatar className="message-avatar">
                            <PersonIcon />
                          </Avatar>
                        )}
                        
                        {!isMine && (
                          <Avatar className="message-avatar">
                            {isManager ? <SupportAgentIcon /> : <PersonIcon />}
                          </Avatar>
                        )}
                        
                        <Box className="message-content">
                          <Typography variant="caption" className="message-sender">
                            {senderName}
                          </Typography>
                          
                          <Card className={`message-bubble${resent ? ' message-resent' : ''}`}>
                            <CardContent>
                              {resent && (
                                <Typography variant="caption" className="message-resent-note">
                                  Sent again
                                </Typography>
                              )}
                              <Typography variant="body2">
                                {message.Message.Message_Body}
                              </Typography>
                            </CardContent>
                          </Card>
                          
                          <Typography variant="caption" className="message-time">
                            {formatMessageTime(message.Message.Message_CreatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input */}
          <Box className="message-input-container">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              variant="outlined"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              className="message-input"
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="send-button"
            >
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>

        {/* Snackbar for errors */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Snackbar for success */}
        <Snackbar 
          open={!!success} 
          autoHideDuration={3000} 
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default UserMessagesView;